import React, { useEffect, useState } from "react";
import { getToken } from "../api";

const API_BASE = process.env.REACT_APP_API_BASE || "http://127.0.0.1:3001";

export default function CaseFiles() {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    loadCases();
  }, []);

  function handleUnauthorized() {
    localStorage.removeItem("token");
    localStorage.removeItem("access_token");
    localStorage.removeItem("user");

    window.location.replace("/login?reason=session-expired");
  }

  async function loadCases() {
    try {
      setLoading(true);
      setError("");

      const token = getToken();

      if (!token) {
        handleUnauthorized();
        return;
      }

      const response = await fetch(
        `${API_BASE}/credit-cases`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 401) {
        handleUnauthorized();
        return;
      }

      if (response.status === 403) {
        const data = await response.json().catch(() => ({}));

        setCases([]);
        setError(
          data?.detail ||
            "Professional plan or higher required."
        );
        return;
      }

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));

        throw new Error(
          data?.detail ||
            data?.message ||
            `Unable to load case files (${response.status}).`
        );
      }

      const data = await response.json();

      setCases(
        Array.isArray(data)
          ? data
          : []
      );
    } catch (err) {
      console.error("Failed to load case files:", err);

      setCases([]);
      setError(
        err?.message ||
          "Unable to load saved case files."
      );
    } finally {
      setLoading(false);
    }
  }

  async function handleDownloadCasePacket(caseId) {
    try {
      setDownloadingId(caseId);
      setError("");

      const token = getToken();

      if (!token) {
        handleUnauthorized();
        return;
      }

      const response = await fetch(
        `${API_BASE}/credit-cases/${caseId}/pdf`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 401) {
        handleUnauthorized();
        return;
      }

      if (response.status === 403) {
        const data = await response.json().catch(() => ({}));

        setError(
          data?.detail ||
            "Professional plan or higher required."
        );
        return;
      }

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));

        throw new Error(
          data?.detail ||
            data?.message ||
            `Unable to download case packet (${response.status}).`
        );
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const downloadLink = document.createElement("a");
      downloadLink.href = url;
      downloadLink.download = `credit_case_${caseId}_packet.pdf`;

      document.body.appendChild(downloadLink);
      downloadLink.click();
      downloadLink.remove();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download case packet:", err);

      setError(
        err?.message ||
          "Unable to download the case packet."
      );
    } finally {
      setDownloadingId(null);
    }
  }

  function handleOpenCase(item) {
    localStorage.setItem(
      "open_case_id",
      String(item.id)
    );

    if (item.client_id) {
      window.location.href =
        `/credit-reports?client_id=${item.client_id}`;
      return;
    }

    window.location.href = "/credit-reports";
  }

  return (
    <div
      style={{
        padding: "28px",
        minHeight: "100vh",
        background: "#f3f6fb",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "16px",
          flexWrap: "wrap",
          marginBottom: "28px",
        }}
      >
        <div>
          <div
            style={{
              color: "#64748b",
              fontSize: "16px",
              fontWeight: "700",
              marginBottom: "6px",
            }}
          >
            Integrity Suite CRM
          </div>

          <h1
            style={{
              margin: 0,
              fontSize: "48px",
              fontWeight: "900",
              color: "#0f172a",
            }}
          >
            📂 Case Files
          </h1>
        </div>

        <button
          type="button"
          onClick={loadCases}
          disabled={loading}
          style={{
            padding: "13px 18px",
            borderRadius: "14px",
            border: "none",
            background: "#0f172a",
            color: "white",
            fontWeight: "800",
            cursor: loading ? "not-allowed" : "pointer",
            opacity: loading ? 0.65 : 1,
          }}
        >
          {loading ? "Refreshing..." : "Refresh Cases"}
        </button>
      </div>

      {error ? (
        <div
          style={{
            padding: "16px 18px",
            marginBottom: "22px",
            borderRadius: "16px",
            border: "1px solid #fecaca",
            background: "#fef2f2",
            color: "#991b1b",
            fontWeight: "700",
          }}
        >
          {error}
        </div>
      ) : null}

      {loading ? (
        <div
          style={{
            background: "white",
            border: "1px solid #e2e8f0",
            borderRadius: "20px",
            padding: "26px",
            color: "#64748b",
            fontWeight: "700",
          }}
        >
          Loading saved case files...
        </div>
      ) : cases.length === 0 ? (
        <div
          style={{
            background: "white",
            border: "1px solid #e2e8f0",
            borderRadius: "20px",
            padding: "26px",
            color: "#64748b",
          }}
        >
          No saved cases.
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gap: "20px",
          }}
        >
          {cases.map((item) => (
            <div
              key={item.id}
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: "20px",
                padding: "24px",
                background: "white",
                boxShadow:
                  "0 8px 22px rgba(15,23,42,0.05)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: "16px",
                  flexWrap: "wrap",
                }}
              >
                <div>
                  <div
                    style={{
                      color: "#64748b",
                      fontSize: "13px",
                      fontWeight: "800",
                      textTransform: "uppercase",
                      letterSpacing: "1px",
                      marginBottom: "8px",
                    }}
                  >
                    Case #{item.id}
                  </div>

                  <h3
                    style={{
                      margin: 0,
                      fontSize: "26px",
                      color: "#0f172a",
                    }}
                  >
                    {item.account_name || "Saved Credit Case"}
                  </h3>
                </div>

                <div
                  style={{
                    padding: "8px 12px",
                    borderRadius: "999px",
                    background: "#fff7ed",
                    color: "#c2410c",
                    fontWeight: "900",
                  }}
                >
                  {item.priority || "Standard"}
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: "14px",
                  marginTop: "22px",
                }}
              >
                <CaseField
                  label="Bureau"
                  value={item.bureau || "Not specified"}
                />

                <CaseField
                  label="Violation"
                  value={
                    item.primary_violation ||
                    "Accuracy review"
                  }
                />

                <CaseField
                  label="Recommended Letter"
                  value={
                    item.recommended_letter ||
                    "General Dispute"
                  }
                />

                <CaseField
                  label="Status"
                  value={
                    item.case_status ||
                    "Active"
                  }
                />
              </div>

              {item.next_step ? (
                <div
                  style={{
                    marginTop: "20px",
                    padding: "16px",
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    borderRadius: "14px",
                    color: "#475569",
                    lineHeight: 1.6,
                  }}
                >
                  <strong style={{ color: "#0f172a" }}>
                    Next Step:
                  </strong>{" "}
                  {item.next_step}
                </div>
              ) : null}

              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  flexWrap: "wrap",
                  marginTop: "22px",
                }}
              >
                <button
                  type="button"
                  onClick={() =>
                    handleDownloadCasePacket(item.id)
                  }
                  disabled={downloadingId === item.id}
                  style={{
                    padding: "11px 15px",
                    borderRadius: "11px",
                    border: "none",
                    background: "#D4AF37",
                    color: "#ffffff",
                    fontWeight: "800",
                    cursor:
                      downloadingId === item.id
                        ? "not-allowed"
                        : "pointer",
                    opacity:
                      downloadingId === item.id
                        ? 0.65
                        : 1,
                  }}
                >
                  {downloadingId === item.id
                    ? "Preparing PDF..."
                    : "📄 Download Full Case Packet"}
                </button>

                <button
                  type="button"
                  onClick={() => handleOpenCase(item)}
                  style={{
                    padding: "11px 15px",
                    borderRadius: "11px",
                    border: "none",
                    background: "#16a34a",
                    color: "white",
                    fontWeight: "800",
                    cursor: "pointer",
                  }}
                >
                  👁 Open Case
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CaseField({ label, value }) {
  return (
    <div
      style={{
        padding: "14px",
        borderRadius: "14px",
        background: "#f8fafc",
        border: "1px solid #e2e8f0",
      }}
    >
      <div
        style={{
          color: "#64748b",
          fontSize: "12px",
          fontWeight: "800",
          textTransform: "uppercase",
          letterSpacing: "0.8px",
          marginBottom: "6px",
        }}
      >
        {label}
      </div>

      <div
        style={{
          color: "#0f172a",
          fontWeight: "800",
          lineHeight: 1.4,
        }}
      >
        {value}
      </div>
    </div>
  );
}
